import type { MisconceptionDefinition } from '../types'

/**
 * TIJD — Tijdsduur / schema / planning / diagram sub-registry.
 *
 * Misconcepties rond het rekenen met tijdsverschillen tussen twee
 * tijdstippen: aftrekken/optellen van minuten, uurgrenzen oversteken,
 * buffer- en wachttijden, deel-uren, en het aflezen van schema's en
 * afstand-tijd-diagrammen.
 *
 * Families: TIJD-DUUR, TIJD-SCHEMA, TIJD-PLANNING, TIJD-DIAGRAM.
 *
 * Onderdeel van de TIJD-barrel-splitsing (juni 2026).
 */
export const TIJD_DUUR_MISCONCEPTIONS: MisconceptionDefinition[] = [
  // ─── Optellen / aftrekken van minuten ───────────────────────────────────
  {
    id: 'tijd.minutes-subtraction-error',
    label: 'Minuten verkeerd afgetrokken',
    module: 'Tijd',
    description:
      'Leerling trekt minuten verkeerd af bij tijdsduur-berekeningen (te weinig of te veel).',
    typicalFamilies: ['TIJD-SCHEMA', 'TIJD-DUUR', 'TIJD-DIAGRAM'],
  },
  {
    id: 'tijd.minutes-addition-error',
    label: 'Minuten verkeerd opgeteld',
    module: 'Tijd',
    description:
      'Leerling telt minuten verkeerd op bij eindtijd- of wachttijd-berekeningen.',
    typicalFamilies: ['TIJD-SCHEMA', 'TIJD-PLANNING', 'TIJD-DIAGRAM'],
  },
  {
    id: 'tijd.addition-error-minutes',
    label: 'Rekenfout bij optellen van deelminuten',
    module: 'Tijd',
    description:
      'Leerling maakt een rekenfout bij het optellen van twee groepen minuten (bijv. 45 + 40 = 95 i.p.v. 85).',
    typicalFamilies: ['TIJD-DUUR', 'TIJD-PLANNING'],
  },

  // ─── Uurgrens & deeluren ────────────────────────────────────────────────
  {
    id: 'tijd.cross-hour-only-first-part',
    label: 'Uurgrens: alleen eerste deel meegeteld',
    module: 'Tijd',
    description:
      'Bij een tijdsduur over de uurgrens telt de leerling alleen het deel tot het hele uur (of alleen het deel ná het hele uur).',
    typicalFamilies: [
      'TIJD-SCHEMA',
      'TIJD-DUUR',
      'TIJD-PLANNING',
      'TIJD-DIAGRAM',
    ],
  },
  {
    id: 'tijd.extra-hour-added',
    label: 'Extra uur ten onrechte meegeteld',
    module: 'Tijd',
    description:
      'Leerling telt een extra uur mee terwijl begin- en eindtijd in hetzelfde uur (of binnen 1 uur) liggen.',
    typicalFamilies: ['TIJD-SCHEMA', 'TIJD-DUUR'],
  },
  {
    id: 'tijd.full-hour-instead-of-minutes',
    label: 'Heel uur i.p.v. minuten gerekend',
    module: 'Tijd',
    description:
      'Leerling trekt een heel uur af of telt een heel uur op terwijl de vraag om minuten gaat.',
    typicalFamilies: ['TIJD-SCHEMA', 'TIJD-PLANNING'],
  },
  {
    id: 'tijd.ignores-partial-hour',
    label: 'Deel-uur genegeerd in tijdsduur',
    module: 'Tijd',
    description:
      'Leerling telt alleen hele uren en negeert de minuten boven of onder het hele uur bij duur-berekeningen.',
    typicalFamilies: ['TIJD-DUUR', 'TIJD-SAMENGESTELD'],
  },
  {
    id: 'tijd.rounds-up-to-full-hour',
    label: 'Tijdsduur opgerond naar heel uur',
    module: 'Tijd',
    description:
      'Leerling rondt een gemengde tijdsduur naar boven af op een heel uur (bijv. 1u30 → 2 uur).',
    typicalFamilies: ['TIJD-DUUR'],
  },
  {
    id: 'tijd.counts-only-minutes',
    label: 'Alleen minuten geteld bij duur',
    module: 'Tijd',
    description:
      'Leerling telt alleen het minuten-deel van de duur en vergeet het hele uur.',
    typicalFamilies: ['TIJD-DUUR'],
  },
  {
    id: 'tijd.wrong-minutes-to-hour',
    label: 'Minuten tot heel uur verkeerd geteld',
    module: 'Tijd',
    description:
      'Leerling berekent het aantal minuten van een startpunt tot het volgende hele uur fout (bijv. :45 → :00 als 5 min i.p.v. 15 min).',
    typicalFamilies: ['TIJD-DUUR', 'TIJD-PLANNING'],
  },
  {
    id: 'tijd.counts-too-many-hours',
    label: 'Te veel hele uren geteld',
    module: 'Tijd',
    description:
      'Leerling telt meer hele uren dan er werkelijk tussen begin- en eindtijd liggen.',
    typicalFamilies: ['TIJD-DUUR'],
  },
  {
    id: 'tijd.wrong-minutes-read',
    label: 'Verkeerd aantal minuten afgelezen',
    module: 'Tijd',
    description:
      'Leerling leest een verkeerd aantal minuten uit de opgave (bijv. 15 i.p.v. 45 minuten).',
    typicalFamilies: ['TIJD-DUUR'],
  },
  {
    id: 'tijd.confuses-half-quarter',
    label: 'Half en kwart verward bij duur',
    module: 'Tijd',
    description:
      'Leerling verwart 30 minuten (half) met 45 minuten (drie kwartier) of 15 minuten (kwart) bij duur-berekeningen.',
    typicalFamilies: ['TIJD-DUUR'],
  },

  // ─── Planning & richtingsfouten ─────────────────────────────────────────
  {
    id: 'tijd.add-instead-of-subtract',
    label: 'Optellen i.p.v. aftrekken bij "eerder"',
    module: 'Tijd',
    description:
      'Bij vragen waarin de leerling eerder moet zijn of terug moet rekenen, telt zij de tijd op in plaats van af te trekken.',
    typicalFamilies: ['TIJD-SCHEMA', 'TIJD-PLANNING'],
  },
  {
    id: 'tijd.buffer-time-forgotten',
    label: 'Buffertijd vergeten',
    module: 'Tijd',
    description:
      'Leerling vergeet bij plannings-vraagstukken de buffer- of inchecktijd mee te rekenen.',
    typicalFamilies: ['TIJD-PLANNING'],
  },
  {
    id: 'tijd.overflow-notation-error',
    label: 'Overflow-notatie fout (>60 minuten)',
    module: 'Tijd',
    description:
      'Leerling schrijft >60 minuten direct achter de dubbele punt (bijv. "13:75" of "11:70") in plaats van om te rekenen naar uren en minuten.',
    typicalFamilies: ['TIJD-PLANNING', 'TIJD-DUUR'],
  },

  // ─── Diagram-lezen ──────────────────────────────────────────────────────
  {
    id: 'tijd.diagram-rise-vs-fall-confused',
    label: 'Stijging vs. daling verward in diagram',
    module: 'Tijd',
    description:
      'Leerling herkent het verschil tussen een stijgende en dalende lijn in een tijddiagram niet, of telt een daling als stijging.',
    typicalFamilies: ['TIJD-DIAGRAM'],
  },
  {
    id: 'tijd.diagram-speed-vs-distance-confused',
    label: 'Snelheid vs. afstand verward in afstand-tijd-grafiek',
    module: 'Tijd',
    description:
      'In een afstand-tijd diagram trekt de leerling de verkeerde conclusie over snelheid (bijv. minder afstand = sneller).',
    typicalFamilies: ['TIJD-DIAGRAM'],
  },
  {
    id: 'tijd.diagram-departure-vs-arrival-confused',
    label: 'Vertrek- vs. aankomsttijd verward',
    module: 'Tijd',
    description:
      'Leerling vergelijkt vertrektijden in plaats van aankomsttijden (of andersom) bij vergelijkings-vragen.',
    typicalFamilies: ['TIJD-DIAGRAM', 'TIJD-SCHEMA'],
  },
]
